import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Select, MenuItem, Typography, Box, Chip } from "@mui/material";
import Dropzone from "react-dropzone";
import * as XLSX from "xlsx";
import { MdClose, MdWarningAmber } from "react-icons/md";
import successimage from "../../../assets/images/sucessImge.png";
import classes from "./myteam.module.css";
import { useDispatch, useSelector } from "react-redux";
import { authApi, getAuthorizationHeader } from "../../../api-calls/user-requests";
import { ADD_DEPARTMENT, IMPORT_MEMBERS, TEAM_LIST } from "../../../api-calls/api-urls";
import { departmentLists } from "../../../Redux/actions/team-actions";
import AlertBox from "../../../components/AlertBox";

const FIELD_KEYWORDS = {
  name: ["name"],
  mobile: ["mobile", "phone", "contact"],
  department: ["department", "dept"],
};

const guessColumn = (headers, keywords) => {
  const idx = headers.findIndex((h) =>
    keywords.some((k) => h.toLowerCase().includes(k))
  );
  return idx >= 0 ? String(idx) : "";
};

const normalizeMobile = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

// Sentinel for "no existing department matched this sheet value" — the
// default going forward is to auto-create a new department for it (server
// picks a unique color from the app's palette), not to drop it silently.
const AUTO_CREATE_DEPARTMENT = "__AUTO_CREATE__";

const ImportMembersModal = (props) => {
  const dispatch = useDispatch();
  const rootRef = React.useRef(null);
  const departmentList = useSelector((state) => state.teams.departmentList);

  const [step, setStep] = useState("upload");
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({ name: "", mobile: "", department: "" });
  const [departmentValueMap, setDepartmentValueMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const getDepartmentData = async () => {
      try {
        const { data } = await authApi.get(ADD_DEPARTMENT, {
          headers: { Authorization: getAuthorizationHeader() },
        });
        dispatch(departmentLists(data));
      } catch (err) {
        // Intentionally ignored — not surfaced to the user
      }
    };
    getDepartmentData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    props.modalClose(false);
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    setFileError("");
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: "",
        });
        const nonEmptyRows = json.filter((row) =>
          row.some((cell) => String(cell).trim() !== "")
        );
        if (nonEmptyRows.length < 2) {
          setFileError("This file doesn't have any data rows to import.");
          return;
        }
        const parsedHeaders = nonEmptyRows[0].map(
          (h, i) => String(h).trim() || `Column ${i + 1}`
        );
        const dataRows = nonEmptyRows.slice(1);
        setHeaders(parsedHeaders);
        setRawRows(dataRows);
        setFileName(file.name);
        setDepartmentValueMap({});
        setMapping({
          name: guessColumn(parsedHeaders, FIELD_KEYWORDS.name),
          mobile: guessColumn(parsedHeaders, FIELD_KEYWORDS.mobile),
          department: guessColumn(parsedHeaders, FIELD_KEYWORDS.department),
        });
        setStep("mapping");
      } catch (err) {
        setFileError("Couldn't read this file. Please upload a valid Excel or CSV file.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const mappingComplete = mapping.name !== "" && mapping.mobile !== "";

  const parsedRows = useMemo(() => {
    if (!mappingComplete) return [];
    const nameIdx = Number(mapping.name);
    const mobileIdx = Number(mapping.mobile);
    const deptIdx = mapping.department !== "" ? Number(mapping.department) : null;
    return rawRows.map((row) => ({
      user_name: String(row[nameIdx] ?? "").trim(),
      mobile_number: normalizeMobile(row[mobileIdx]),
      departmentRaw: deptIdx !== null ? String(row[deptIdx] ?? "").trim() : "",
    }));
  }, [rawRows, mapping, mappingComplete]);

  const uniqueDepartmentValues = useMemo(() => {
    const values = new Set();
    parsedRows.forEach((r) => {
      if (r.departmentRaw) values.add(r.departmentRaw);
    });
    return Array.from(values);
  }, [parsedRows]);

  const hasDepartmentStep = mapping.department !== "" && uniqueDepartmentValues.length > 0;

  useEffect(() => {
    if (!uniqueDepartmentValues.length || !departmentList) return;
    setDepartmentValueMap((prev) => {
      const next = { ...prev };
      let changed = false;
      uniqueDepartmentValues.forEach((value) => {
        if (next[value] !== undefined) return;
        const match = departmentList.find(
          (d) => d.department_name.toLowerCase() === value.toLowerCase()
        );
        next[value] = match ? match._id : AUTO_CREATE_DEPARTMENT;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [uniqueDepartmentValues, departmentList]);

  const { readyRows, clientSkipped } = useMemo(() => {
    const ready = [];
    const skipped = [];
    const seenMobiles = new Set();
    parsedRows.forEach((r) => {
      if (!r.user_name) {
        skipped.push({ ...r, reason: "Missing name" });
        return;
      }
      if (!/^[6-9][0-9]{9}$/.test(r.mobile_number)) {
        skipped.push({ ...r, reason: "Invalid mobile number" });
        return;
      }
      if (seenMobiles.has(r.mobile_number)) {
        skipped.push({ ...r, reason: "Duplicate mobile number in file" });
        return;
      }
      seenMobiles.add(r.mobile_number);
      const selectedDept = r.departmentRaw ? departmentValueMap[r.departmentRaw] : undefined;
      ready.push({
        user_name: r.user_name,
        mobile_number: r.mobile_number,
        // Sentinel -> ask the backend to find-or-create a department by this
        // raw name. A real id -> use that existing department as-is.
        // Explicit "" ("No department") or no departmentRaw at all -> neither field.
        ...(selectedDept === AUTO_CREATE_DEPARTMENT
          ? { department_name: r.departmentRaw }
          : selectedDept
            ? { department: selectedDept }
            : {}),
      });
    });
    return { readyRows: ready, clientSkipped: skipped };
  }, [parsedRows, departmentValueMap]);

  const handleSubmit = async () => {
    if (!readyRows.length) return;
    setSubmitting(true);
    try {
      const { data } = await authApi.post(
        `${TEAM_LIST}/${props.id}${IMPORT_MEMBERS}`,
        { members: readyRows },
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      setResult(data);
      setStep("result");
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Something went wrong while importing members");
      setShowAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Modal
        open
        onClose={close}
        aria-labelledby="import-members-modal-title"
        data-testid="import_members_modal"
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        container={() => rootRef.current}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: (theme) => theme.shadows[5],
          }}
          style={{ width: "600px", maxHeight: "85vh", overflowY: "auto" }}
          className={classes.loginModalWidth}
        >
          <Button
            variant="text"
            sx={{ minWidth: "unset" }}
            className={classes.closebtn}
            onClick={close}
          >
            <MdClose />
          </Button>

          {step !== "result" && (
            <div className={classes.AddMemberHeading}>
              <Typography>Import Members from Excel</Typography>
            </div>
          )}

          {step === "upload" && (
            <div className={classes.modelInputsContainer}>
              <div className={classes.DropZoneContainer}>
                <Dropzone
                  onDrop={onDrop}
                  multiple={false}
                  accept={{
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                    "application/vnd.ms-excel": [".xls"],
                    "text/csv": [".csv"],
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps({ className: classes.DropZone })}>
                      <input {...getInputProps()} />
                      <Button variant="contained" className={classes.DropzoneBtn}>
                        Browse
                      </Button>
                      <div>Drop an Excel or CSV file here</div>
                    </div>
                  )}
                </Dropzone>
              </div>
              {fileError && (
                <Typography className={classes.successPageSubText} style={{ color: "#c0392b" }}>
                  {fileError}
                </Typography>
              )}
            </div>
          )}

          {step === "mapping" && (
            <div className={classes.modelInputsContainer}>
              <Typography className={classes.form_lable}>
                File: {fileName} ({rawRows.length} row{rawRows.length === 1 ? "" : "s"} found)
              </Typography>
              {["name", "mobile", "department"].map((field) => (
                <div className={classes.inputs} key={field}>
                  <label className={classes.form_lable}>
                    {field === "name" && "Name*"}
                    {field === "mobile" && "Mobile Number*"}
                    {field === "department" && "Department"}
                  </label>
                  <Select
                    fullWidth
                    size="small"
                    value={mapping[field]}
                    displayEmpty
                    onChange={(e) =>
                      setMapping((prev) => ({ ...prev, [field]: e.target.value }))
                    }
                  >
                    <MenuItem value="">
                      <em>{field === "department" ? "Not mapped" : "Select a column"}</em>
                    </MenuItem>
                    {headers.map((h, idx) => (
                      <MenuItem key={idx} value={String(idx)}>
                        {h}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ))}

              {mappingComplete && (
                <div>
                  <Typography className={classes.form_lable}>Preview</Typography>
                  <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "4px" }}>Name</th>
                        <th style={{ textAlign: "left", padding: "4px" }}>Mobile</th>
                        <th style={{ textAlign: "left", padding: "4px" }}>Department</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 5).map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: "4px" }}>{r.user_name || "—"}</td>
                          <td style={{ padding: "4px" }}>{r.mobile_number || "—"}</td>
                          <td style={{ padding: "4px" }}>{r.departmentRaw || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className={classes.addMemberModalbtnSection}>
                <Button
                  variant="outlined"
                  style={{ border: "2px solid #1976d2", color: "#1976d2" }}
                  onClick={() => setStep("upload")}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  disabled={!mappingComplete}
                  onClick={() => setStep(hasDepartmentStep ? "department" : "preview")}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {step === "department" && (
            <div className={classes.modelInputsContainer}>
              <Typography className={classes.form_lable}>
                Match department values found in the sheet to your existing departments, or let
                new ones be created automatically.
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {uniqueDepartmentValues.map((value) => {
                  const selected = departmentValueMap[value] || AUTO_CREATE_DEPARTMENT;
                  const willAutoCreate = selected === AUTO_CREATE_DEPARTMENT;
                  return (
                    <div
                      className={classes.inputs}
                      key={value}
                      style={{
                        border: willAutoCreate ? "1px solid #f5c26b" : "1px solid transparent",
                        background: willAutoCreate ? "#fff8ec" : "transparent",
                        borderRadius: "6px",
                        padding: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <label className={classes.form_lable}>
                        &quot;{value}&quot;
                        {willAutoCreate && (
                          <span style={{ color: "#b26a00", marginLeft: "8px" }}>
                            <MdWarningAmber style={{ verticalAlign: "middle" }} /> No matching department — a new department will be created
                          </span>
                        )}
                      </label>
                      <Select
                        fullWidth
                        size="small"
                        value={selected}
                        displayEmpty
                        onChange={(e) =>
                          setDepartmentValueMap((prev) => ({ ...prev, [value]: e.target.value }))
                        }
                      >
                        <MenuItem value={AUTO_CREATE_DEPARTMENT}>
                          <em>Create new department &quot;{value}&quot;</em>
                        </MenuItem>
                        <MenuItem value="">
                          <em>No department</em>
                        </MenuItem>
                        {departmentList &&
                          departmentList.map((d) => (
                            <MenuItem key={d._id} value={d._id}>
                              {d.department_name}
                            </MenuItem>
                          ))}
                      </Select>
                    </div>
                  );
                })}
              </div>
              <div className={classes.addMemberModalbtnSection}>
                <Button
                  variant="outlined"
                  style={{ border: "2px solid #1976d2", color: "#1976d2" }}
                  onClick={() => setStep("mapping")}
                >
                  Back
                </Button>
                <Button variant="contained" onClick={() => setStep("preview")}>
                  Next
                </Button>
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className={classes.modelInputsContainer}>
              <Typography className={classes.form_lable}>
                {readyRows.length} member{readyRows.length === 1 ? "" : "s"} ready to import
                {clientSkipped.length > 0 && `, ${clientSkipped.length} will be skipped`}
              </Typography>
              {clientSkipped.length > 0 && (
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {clientSkipped.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Typography className={classes.form_lable}>
                        {r.user_name || r.mobile_number || `Row ${i + 1}`}
                      </Typography>
                      <Chip
                        label={r.reason}
                        size="small"
                        style={{ background: "#fdecea", color: "#c0392b" }}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className={classes.addMemberModalbtnSection}>
                <Button
                  variant="outlined"
                  style={{ border: "2px solid #1976d2", color: "#1976d2" }}
                  onClick={() => setStep(hasDepartmentStep ? "department" : "mapping")}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  disabled={!readyRows.length || submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? "Importing..." : `Import ${readyRows.length}`}
                </Button>
              </div>
            </div>
          )}

          {step === "result" && result && (
            <div className={classes.addMembersuccess}>
              <img src={successimage} alt="Success logo" width="auto" height="auto" />
              <Typography className={classes.imagePageText}>
                {result.addedCount} member{result.addedCount === 1 ? "" : "s"} added
              </Typography>
              {result.createdDepartments?.length > 0 && (
                <>
                  <Typography className={classes.successPageSubText}>
                    {result.createdDepartments.length} new department
                    {result.createdDepartments.length === 1 ? "" : "s"} created
                  </Typography>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center" }}>
                    {result.createdDepartments.map((d) => (
                      <Chip
                        key={d._id}
                        label={d.department_name}
                        size="small"
                        style={{ background: d.colour, color: "#fff" }}
                      />
                    ))}
                  </div>
                </>
              )}
              {result.skippedCount > 0 && (
                <>
                  <Typography className={classes.successPageSubText}>
                    {result.skippedCount} skipped
                  </Typography>
                  <div style={{ maxHeight: "150px", overflowY: "auto", width: "100%" }}>
                    {result.skipped.map((r, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 12px",
                        }}
                      >
                        <Typography className={classes.form_lable}>
                          {r.user_name || r.mobile_number}
                        </Typography>
                        <Typography className={classes.form_lable}>{r.reason}</Typography>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <Button variant="contained" onClick={close} style={{ marginTop: "15px" }}>
                Done
              </Button>
            </div>
          )}
        </Box>
      </Modal>
      {showAlert && error && (
        <AlertBox type="error" message={error} stateName={setShowAlert} />
      )}
    </div>
  );
};

export default ImportMembersModal;
